import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Server } from '../../lib/api/servers';

const serverSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'Host is required').max(255),
  port: z.number().min(1).max(65535),
  username: z.string().min(1, 'Username is required').max(255),
  ssh_key: z.string().min(1, 'SSH key is required'),
  is_local: z.boolean(),
  description: z.string().optional(),
});

export type ServerFormData = z.infer<typeof serverSchema>;

interface ServerFormProps {
  server?: Server;
  onSubmit: (data: ServerFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ServerForm({ server, onSubmit, onCancel, isLoading }: ServerFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ServerFormData>({
    resolver: zodResolver(serverSchema),
    defaultValues: server
      ? {
          name: server.name,
          host: server.host,
          port: server.port,
          username: server.username,
          ssh_key: '', // Don't pre-fill SSH key
          is_local: server.is_local,
          description: server.description || '',
        }
      : {
          name: '',
          host: '',
          port: 22,
          username: '',
          ssh_key: '',
          is_local: false,
          description: '',
        },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Server Name"
        placeholder="Production Server"
        error={errors.name?.message}
        {...register('name')}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Host"
          placeholder="192.168.1.100 or server.example.com"
          error={errors.host?.message}
          {...register('host')}
        />
        <Input
          label="Port"
          type="number"
          placeholder="22"
          error={errors.port?.message}
          {...register('port')}
        />
      </div>

      <Input
        label="Username"
        placeholder="ubuntu"
        error={errors.username?.message}
        {...register('username')}
      />

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          SSH Private Key {server && '(leave empty to keep current)'}
        </label>
        <textarea
          className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          rows={6}
          placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----"
          {...register('ssh_key')}
        />
        {errors.ssh_key && (
          <p className="text-sm text-red-600">{errors.ssh_key.message}</p>
        )}
      </div>

      <Input
        label="Description (optional)"
        placeholder="Main production server"
        error={errors.description?.message}
        {...register('description')}
      />

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_local"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          {...register('is_local')}
        />
        <label htmlFor="is_local" className="text-sm text-gray-700">
          This is the local server (no SSH needed)
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {server ? 'Update Server' : 'Add Server'}
        </Button>
      </div>
    </form>
  );
}
